import { useCallback, useRef, useEffect } from 'react';
import { usePlayerStore } from '@/store/playerStore';
import { useRoomStore } from '@/store/roomStore';
import type { SignalingMessage, SyncPayload } from '@/types';

const HARD_SEEK_THRESHOLD = 1.5;  // seconds — larger diff triggers immediate seek
const SYNC_LOCK_MS = 600;          // ms to ignore local events after applying remote sync

interface UseSyncOptions {
  videoRef: React.RefObject<HTMLVideoElement | null>;
  send: (msg: Omit<SignalingMessage, 'timestamp'>) => void;
  participantId: string | null;
  role?: 'host' | 'guest' | null;
}

export function useSync({ videoRef, send, participantId, role }: UseSyncOptions) {
  const { lockSync, isSyncLocked, setCurrentTime, setPlaying } = usePlayerStore();
  const { room, updatePlayback } = useRoomStore();
  const pendingSeek = useRef(false);

  /** Helper to get actual movie timestamp regardless of whether local video or WebRTC stream */
  const getCurrentMovieTime = useCallback(() => {
    const video = videoRef.current;
    if (!video) return usePlayerStore.getState().currentTime || 0;
    // If watching a remote WebRTC stream, local video.currentTime is only elapsed stream time.
    // The actual movie timestamp is in the player store.
    if (video.srcObject) {
      return usePlayerStore.getState().currentTime || 0;
    }
    return video.currentTime || 0;
  }, [videoRef]);

  /** Send a sync event to all other participants */
  const broadcastSync = useCallback(
    (playbackState: 'playing' | 'paused', currentTime: number) => {
      const currentRoom = useRoomStore.getState().room || room;
      if (!currentRoom) return;
      const version = (currentRoom.version || 0) + 1;

      // Update local room store immediately so UI responds without waiting for network echo
      updatePlayback(playbackState, currentTime, version);
      setCurrentTime(currentTime);
      setPlaying(playbackState === 'playing');

      const video = videoRef.current;
      const dur =
        video && !video.srcObject && video.duration > 0
          ? video.duration
          : usePlayerStore.getState().duration || currentRoom.movieDuration;

      send({
        type: 'SYNC',
        from: participantId ?? undefined,
        payload: {
          playbackState,
          currentTime,
          duration: dur > 0 ? dur : undefined,
          version,
        } satisfies SyncPayload,
      });
    },
    [room, send, participantId, updatePlayback, setCurrentTime, setPlaying]
  );

  /** Called when any user (host or guest) plays */
  const onLocalPlay = useCallback(() => {
    if (isSyncLocked()) return;
    const time = getCurrentMovieTime();
    broadcastSync('playing', time);
  }, [broadcastSync, isSyncLocked, getCurrentMovieTime]);

  /** Called when any user (host or guest) pauses */
  const onLocalPause = useCallback(() => {
    if (isSyncLocked()) return;
    const time = getCurrentMovieTime();
    broadcastSync('paused', time);
  }, [broadcastSync, isSyncLocked, getCurrentMovieTime]);

  /** Called when any user seeks (debounced externally) */
  const onLocalSeek = useCallback(
    (time: number) => {
      if (isSyncLocked()) return;
      const video = videoRef.current;
      const isPaused = video ? video.paused : usePlayerStore.getState().isPlaying === false;
      broadcastSync(isPaused ? 'paused' : 'playing', time);
    },
    [broadcastSync, isSyncLocked, videoRef]
  );

  /** Apply a received sync event from another participant */
  const applyRemoteSync = useCallback(
    (payload: SyncPayload) => {
      const video = videoRef.current;
      const currentRoom = useRoomStore.getState().room;

      // Ignore stale messages
      if (currentRoom && payload.version <= currentRoom.version) return;

      lockSync(SYNC_LOCK_MS);
      updatePlayback(payload.playbackState, payload.currentTime, payload.version);
      setCurrentTime(payload.currentTime);
      setPlaying(payload.playbackState === 'playing');

      if (payload.duration && payload.duration > 0) {
        usePlayerStore.getState().setDuration(payload.duration);
        if (currentRoom && currentRoom.movieDuration !== payload.duration) {
          useRoomStore.getState().setRoom({ ...currentRoom, movieDuration: payload.duration });
        }
      }

      if (!video) return;

      // If this client is the host (local video source), apply seek directly to the movie file
      if (!video.srcObject) {
        const diff = Math.abs(video.currentTime - payload.currentTime);
        if (diff > HARD_SEEK_THRESHOLD) {
          pendingSeek.current = true;
          video.currentTime = payload.currentTime;
          pendingSeek.current = false;
        }
      }

      // Sync play/pause state on local video element
      if (payload.playbackState === 'paused' && !video.paused) {
        video.pause();
      } else if (payload.playbackState === 'playing' && video.paused) {
        video.play().catch((err) => {
          console.warn('[Sync] Play failed (possibly blocked by browser autoplay policy):', err);
          if (err.name === 'NotAllowedError') {
            video.muted = true;
            usePlayerStore.getState().setMuted(true);
            video.play()
              .then(() => {
                usePlayerStore.getState().setBuffering(false);
                usePlayerStore.getState().setAutoplayBlocked(false);
              })
              .catch((e) => {
                console.warn('[Sync] Muted retry also failed:', e);
                usePlayerStore.getState().setAutoplayBlocked(true);
                usePlayerStore.getState().setBuffering(false);
              });
          } else {
            usePlayerStore.getState().setAutoplayBlocked(true);
            usePlayerStore.getState().setBuffering(false);
          }
        });
      }
    },
    [lockSync, updatePlayback, setCurrentTime, setPlaying, videoRef]
  );

  /** Host: Send periodic sync heartbeat every 3 seconds while playing so all guests stay aligned */
  useEffect(() => {
    if (role !== 'host') return;
    const interval = setInterval(() => {
      const currentRoom = useRoomStore.getState().room;
      if (currentRoom?.mediaType === 'youtube') {
        const isPlaying = usePlayerStore.getState().isPlaying;
        const curTime = usePlayerStore.getState().currentTime;
        if (isPlaying && curTime > 0) {
          broadcastSync('playing', curTime);
        }
      } else {
        const video = videoRef.current;
        if (video && !video.paused && !video.srcObject) {
          broadcastSync('playing', video.currentTime);
        }
      }
    }, 3000);
    return () => clearInterval(interval);
  }, [role, broadcastSync, videoRef]);

  return { onLocalPlay, onLocalPause, onLocalSeek, applyRemoteSync, broadcastSync };
}
